import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import { RiDeleteBinLine, RiPencilLine } from "react-icons/ri";
import { BsThreeDots } from "react-icons/bs";
import { IconButton } from "../../design-system/components";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import React, { useState } from "react";
import { DeleteDialog, RenameDialog } from "../common/CommonModal";
import AlertComponent from "../common/AlertComponent";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  archiveConversationService,
  renameConversationService,
} from "../../services/conversationServices";
import { useClientId } from "../../context/ClientsProvider";
import { ACTIVE_CONVERSATION_QUERY_KEY } from "../../hooks/chat/constants";

export function ConversationLink({
  to,
  setIsChatStarted,
  name,
  id,
  toggleDrawer,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === to; // Check if current path matches
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const clientId = useClientId();
  const queryClient = useQueryClient();
  const {
    mutateAsync: deleteConversation,
    isError: isDeleteConversationFailed,
    isPending: isDeletingConversation,
    reset: resetDeleteStatus,
  } = useMutation({
    mutationFn: async () => {
      setIsDeleteDialogOpen(false);
      await archiveConversationService(clientId, id);
      await queryClient.invalidateQueries({
        queryKey: ["conversation-list", clientId],
      });
    },
    onSuccess: () => {
      if (!isActive) return;
      // The user deleted the chat they're currently viewing: drop its
      // cached messages and return to the landing page, otherwise the
      // deleted conversation stays on screen at /c/:id.
      queryClient.removeQueries({
        queryKey: [ACTIVE_CONVERSATION_QUERY_KEY, id],
      });
      setIsChatStarted(false);
      navigate(clientId ? `/${encodeURIComponent(clientId)}` : "/");
    },
  });

  const {
    // `mutate` (not `mutateAsync`): the failure surfaces through
    // `isRenameConversationFailed` below, so nothing awaits the result.
    mutate: renameConversation,
    isError: isRenameConversationFailed,
    isPending: isRenamingConversation,
    reset: resetRenameStatus,
  } = useMutation({
    mutationFn: async (newName) => {
      setIsRenameDialogOpen(false);
      await renameConversationService(clientId, id, newName);
      await queryClient.invalidateQueries({
        queryKey: ["conversation-list", clientId],
      });
    },
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className={`conversation-link--wrapper ${isActive ? "active" : ""}`}>
      {isDeletingConversation || isRenamingConversation ? (
        <Skeleton width="100%" />
      ) : (
        <>
          <NavLink
            to={to}
            onClick={() => {
              if (toggleDrawer) {
                toggleDrawer()(false);
              }
              setIsChatStarted(false);
            }}
            className="conversation-text"
          >
            {name}
          </NavLink>
          <IconButton
            label="Conversation options"
            className="icon-button"
            onClick={handleClick}
          >
            <BsThreeDots />
          </IconButton>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            sx={{
              "& .MuiPaper-root": {
                borderRadius: "var(--radius-sm)",
                boxShadow: "none",
                border: "1px solid var(--border-default)",
                marginTop: "0.5rem",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setIsRenameDialogOpen(true);
              }}
              sx={{
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <div className="conversation-menu-item--wrapper">
                <p>Rename</p>
                <div className="menu-icon--wrapper">
                  <RiPencilLine
                    style={{
                      fontSize: "20px",
                    }}
                  />
                </div>
              </div>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setIsDeleteDialogOpen(true);
              }}
              sx={{
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <div className="conversation-menu-item--wrapper conversation-menu-item--danger">
                <p>Delete chat</p>
                <div className="menu-icon--wrapper">
                  <RiDeleteBinLine
                    style={{
                      fontSize: "20px",
                    }}
                  />
                </div>
              </div>
            </MenuItem>
          </Menu>
        </>
      )}
      {isRenameDialogOpen && (
        <RenameDialog
          isOpen={isRenameDialogOpen}
          currentName={name}
          handleClose={() => setIsRenameDialogOpen(false)}
          onRename={renameConversation}
        />
      )}
      {isRenameConversationFailed && (
        <AlertComponent
          open={isRenameConversationFailed}
          setOpen={resetRenameStatus}
          type={"error"}
          message={"Failed to rename conversation."}
          position={{ vertical: "top", horizontal: "center" }}
          reset={resetRenameStatus}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteDialog
          conversationList={[
            {
              id,
              name,
            },
          ]}
          isOpen={isDeleteDialogOpen}
          handleClose={() => setIsDeleteDialogOpen(false)}
          onDelete={deleteConversation}
        />
      )}
      {isDeleteConversationFailed && (
        <AlertComponent
          open={isDeleteConversationFailed}
          setOpen={resetDeleteStatus}
          type={"error"}
          message={"Failed to delete conversation."}
          position={{ vertical: "top", horizontal: "center" }}
          reset={resetDeleteStatus}
        />
      )}
    </div>
  );
}
